import { Component, ElementRef, ViewChildren, inject, QueryList } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';

import { RegistrationFormService } from '../../../services/registration-form.service';

type UploadSlot = {
  name: string;
  file: File | null;
};

@Component({
  selector: 'app-public-qca-registration',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink],
  templateUrl: './public-qca-registration.component.html',
  styleUrls: ['./public-qca-registration.component.scss']
})
export class PublicQcaRegistrationComponent {
  private readonly fb = inject(FormBuilder);
  private readonly formService = inject(RegistrationFormService);
  private readonly router = inject(Router);
  @ViewChildren('fileInput') private fileInputs!: QueryList<ElementRef<HTMLInputElement>>;

  message = '';
  messageType: 'success' | 'error' | '' = '';
  readonly form = this.fb.nonNullable.group({
    qca_name: ['', [Validators.required]],
    username: [{ value: 'Auto-generated', disabled: true }],
    registration_number: ['QCA-MH-2024-001', [Validators.required]],
    pss_selection: ['', [Validators.required]],
    address_line1: ['', [Validators.required]],
    address_line2: [''],
    city_town: ['', [Validators.required]],
    district: ['', [Validators.required]],
    state: ['', [Validators.required]],
    pin_code: ['', [Validators.required]],
    country: ['India', [Validators.required]],
    control_room_contact_no: ['', [Validators.required]],
    control_room_mobile: ['', [Validators.required]],
    control_room_email: ['', [Validators.required, Validators.email]],
    control_room_fax: [''],
    control_room_address: ['', [Validators.required]],
    nodal_full_name: ['', [Validators.required]],
    nodal_designation: ['', [Validators.required]],
    nodal_mobile_number: ['', [Validators.required]],
    nodal_email_address: ['', [Validators.required, Validators.email]],
    nodal_alternate_mobile: [''],
    nodal_alternate_email: [''],
    alternate_full_name: [''],
    alternate_designation: [''],
    alternate_mobile_number: [''],
    alternate_email_address: [''],
    declaration_accept: [false, [Validators.requiredTrue]]
  });

  readonly documents = [
    'QCA Designation Letter / Certificate',
    'Authorization Letter from Generating Company',
    'Board Resolution / Power of Attorney'
  ];
  readonly uploadSlots: UploadSlot[] = this.documents.map((name) => ({ name, file: null }));

  openFilePicker(index: number): void {
    const input = this.fileInputs?.get(index)?.nativeElement;
    input?.click();
  }

  onFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.uploadSlots[index].file = file;
  }

  reset(): void {
    this.form.reset({
      qca_name: '',
      username: 'Auto-generated',
      registration_number: 'QCA-MH-2024-001',
      pss_selection: '',
      address_line1: '',
      address_line2: '',
      city_town: '',
      district: '',
      state: '',
      pin_code: '',
      country: 'India',
      control_room_contact_no: '',
      control_room_mobile: '',
      control_room_email: '',
      control_room_fax: '',
      control_room_address: '',
      nodal_full_name: '',
      nodal_designation: '',
      nodal_mobile_number: '',
      nodal_email_address: '',
      nodal_alternate_mobile: '',
      nodal_alternate_email: '',
      alternate_full_name: '',
      alternate_designation: '',
      alternate_mobile_number: '',
      alternate_email_address: '',
      declaration_accept: false
    });
    this.uploadSlots.forEach((slot) => (slot.file = null));
    this.message = '';
    this.messageType = '';
  }

  private fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          resolve(result.split(',')[1] || '');
          return;
        }
        reject(new Error('Unable to read file.'));
      };
      reader.onerror = () => reject(new Error('Unable to read file.'));
      reader.readAsDataURL(file);
    });
  }

  private getApiMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      return typeof error.error?.message === 'string'
        ? error.error.message
        : typeof error.message === 'string' && error.message
          ? error.message
          : 'Unable to submit QCA registration.';
    }
    return 'Unable to submit QCA registration.';
  }

  private buildUsername(entityName: string): string {
    const slug = entityName.toLowerCase().replace(/[^a-z0-9]+/g, '.').replace(/^\.+|\.+$/g, '');
    return `${slug || 'qca'}-${Date.now().toString(36)}`;
  }

  getErrorMessage(controlName: string): string {
    const control = this.form.get(controlName);
    if (!control || !control.touched || !control.errors) {
      return '';
    }

    if (control.errors['required']) {
      return 'This field is required.';
    }
    if (control.errors['email']) {
      return 'Please enter a valid email address.';
    }
    if (control.errors['requiredTrue']) {
      return 'Please accept the declaration to continue.';
    }
    return 'Please check this field.';
  }

  async submit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.message = 'Please fix the highlighted fields before submitting.';
      this.messageType = 'error';
      return;
    }

    const documents = await Promise.all(this.uploadSlots
      .filter((slot) => slot.file)
      .map(async (slot) => {
        const file = slot.file as File;
        return {
          file_name: file.name,
          file_type: file.type || 'application/octet-stream',
          file_size: file.size,
          file_data_base64: await this.fileToBase64(file),
          document_type: slot.name
        };
      }));

    const raw = this.form.getRawValue();
    const entityName = raw.qca_name || 'QCA Registration';
    this.formService.submit({
      entity_type: 'QCA_REGISTRATION',
      entity_name: entityName,
      username: this.buildUsername(entityName),
      email: raw.control_room_email || raw.nodal_email_address || '',
      mobile: raw.control_room_mobile || null,
      payload_json: {
        ...raw,
        category: 'QCA',
        documents: this.documents,
        declaration_accept: raw.declaration_accept
      },
      documents: documents.map((file) => ({
        document_type: file.document_type,
        file_name: file.file_name,
        file_type: file.file_type,
        file_data_base64: file.file_data_base64
      }))
    }).subscribe({
      next: (response) => {
        this.message = 'QCA registration submitted successfully.';
        this.messageType = 'success';
        void this.router.navigate(['/register/qca/confirmation'], {
          state: {
            registrationNo: response.value === '' ? 'N/A' : response.value.registration_no,
            stakeholderType: 'QCA Registration',
            submittedOn: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
            status: 'Pending Admin Review'
          }
        });
      },
      error: (error: HttpErrorResponse) => {
        this.message = this.getApiMessage(error);
        this.messageType = 'error';
      }
    });
  }
}

